import {
  PaymentStatus,
  type PaymentTransaction,
  PaymentType,
} from 'api/payments';
import classnames from 'classnames';
import Icon from 'components/Icon/Icon';
import Tooltip from 'components/Tooltip';
import type { FC } from 'react';
import useLayout, { LayoutType } from 'utils/layout';
import { capitalize } from 'utils/string';
import { getTimeFromTimestamp } from 'utils/time';
import { Translation, useTranslation } from 'utils/translations';
import useIntl from 'utils/useIntl';

import styles from './TransactionsItem.module.scss';

type TransactionsItemProps = {
  transaction: PaymentTransaction;
};

const TransactionsItem: FC<TransactionsItemProps> = ({ transaction }) => {
  const { formatMoney, formatDate } = useIntl();
  const layout = useLayout();

  const status = useTranslation('transaction_status');
  const amount = useTranslation('transaction_amount');
  const date = useTranslation('date');
  const type = useTranslation('type');
  const methodName = useTranslation('method');
  const method = capitalize(methodName);
  const failedTransactionMessage = useTranslation(
    'transaction_declined_message',
  );

  return (
    <div
      className={classnames(styles['transactions-list-item'], {
        [styles['transactions-list-item-desktop']]:
          layout !== LayoutType.mobile,
      })}
    >
      <div
        className={classnames(styles['transactions-item-cell'], {
          [styles['transactions-item-cell-desktop']]:
            layout !== LayoutType.mobile,
          [styles['transactions-item-cell-without-border']]:
            layout !== LayoutType.mobile,
          [styles['transactions-item-date']]: layout === LayoutType.mobile,
          [styles['dynamic-text']]: layout === LayoutType.mobile,
        })}
        data-text={date}
      >
        <span className={styles['primary-text']}>
          {formatDate(transaction.createDate)}
        </span>
        <span className={styles['secondary-text']}>
          {getTimeFromTimestamp(transaction.createDate)}
        </span>
      </div>
      <div
        className={classnames(styles['transactions-item-cell'], {
          [styles['transactions-item-cell-desktop']]:
            layout !== LayoutType.mobile,
          [styles['transactions-item-cell-without-border']]:
            layout !== LayoutType.mobile,
          [styles['transactions-item-type']]: layout === LayoutType.mobile,
          [styles['dynamic-text']]: layout === LayoutType.mobile,
        })}
        data-text={type}
      >
        <div
          className={classnames(styles['transactions-type-date'], {
            [styles['transactions-type-date-desktop']]:
              layout !== LayoutType.mobile,
          })}
        >
          <span className={styles['primary-text']}>
            <Translation message={transaction.operationType.toLowerCase()} />
          </span>
          <span className={styles['secondary-text']}>{transaction.id}</span>
        </div>
      </div>
      <div
        className={classnames(styles['transactions-item-cell'], {
          [styles['transactions-item-cell-desktop']]:
            layout !== LayoutType.mobile,
          [styles['transactions-item-cell-without-border']]:
            layout !== LayoutType.mobile,
          [styles['transactions-item-method']]: layout === LayoutType.mobile,
          [styles['dynamic-text']]: layout === LayoutType.mobile,
        })}
        data-text={method}
      >
        <span className={styles['secondary-text']}>
          {transaction.paymentMethodName}
        </span>
      </div>
      <div
        className={classnames(
          styles['transactions-item-cell'],
          styles['transactions-item-amount'],
          {
            [styles['transactions-item-cell-desktop']]:
              layout !== LayoutType.mobile,
            [styles['dynamic-text']]: layout === LayoutType.mobile,
          },
        )}
        data-text={amount}
      >
        <span
          className={classnames(styles['primary-text'], {
            [styles['primary-text-mobile']]: layout === LayoutType.mobile,
          })}
        >
          {transaction.operationType === PaymentType.DEPOSIT ? '+' : '-'}&nbsp;
          {formatMoney(transaction.amount)}
        </span>
      </div>
      <div
        className={classnames(styles['transactions-item-cell'], {
          [styles['transactions-item-cell-desktop']]:
            layout !== LayoutType.mobile,
          [styles['dynamic-text']]: layout === LayoutType.mobile,
        })}
        data-text={status}
      >
        <span
          className={classnames(styles['transactions-item-status'], {
            [styles[`status-${transaction.status.toLowerCase()}`]]:
              transaction.status,
          })}
        >
          {capitalize(transaction.status)}
          {transaction.status === PaymentStatus.FAILED && (
            <Tooltip message={failedTransactionMessage}>
              <Icon
                className={styles['transactions-item-status-icon']}
                name="information.outline"
              />
            </Tooltip>
          )}
        </span>
      </div>
    </div>
  );
};

export default TransactionsItem;
